import API from "./api";
import ExportHelper from "./exportHelper";

const StudentService = {
  // Get all students with payment summary (admin)
  getAllStudents: async () => {
    try {
      const response = await API.get("/admin/students");
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get student details with courses and transactions (admin)
  getStudentDetails: async (studentId) => {
    try {
      const response = await API.get(`/admin/students/${studentId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get available students for a course (not enrolled)
  getAvailableStudents: async (courseId) => {
    try {
      const response = await API.get(`/admin/students/available/${courseId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Add new student manually (auto password)
  addStudentManually: async (studentData) => {
    try {
      const response = await API.post("/admin/students/manual", studentData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Enroll existing student in course
  enrollExistingStudent: async (studentId, courseId) => {
    try {
      const response = await API.post(`/admin/students/${studentId}/enroll`, {
        course_id: courseId,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Add payment for student (admin records)
  addPayment: async (studentId, paymentData) => {
    try {
      console.log("API Call - addPayment:", { studentId, paymentData });
      const response = await API.post(
        `/admin/students/${studentId}/payment`,
        paymentData,
      );
      return response.data;
    } catch (error) {
      console.error("API Error - addPayment:", error.response?.data || error);
      throw error;
    }
  },

  // Get receipt data (FOR ADMIN - from admin panel)
  getAdminReceipt: async (transactionId) => {
    try {
      const response = await API.get(`/admin/receipt/${transactionId}`);
      console.log("✅ Admin Receipt data:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ Error fetching admin receipt:", error);
      throw error;
    }
  },

  // Get receipt data (FOR STUDENT - from student panel)
  getStudentReceipt: async (transactionId) => {
    try {
      const studentId = JSON.parse(localStorage.getItem("user"))?.id;
      const response = await API.get(
        `/students/${studentId}/receipt/${transactionId}`,
      );
      console.log("✅ Student Receipt data:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ Error fetching student receipt:", error);
      throw error;
    }
  },

  // Get pending verification requests (existing)
  getPendingRequests: async () => {
    try {
      const response = await API.get("/auth/pending");
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Verify student (existing)
  verifyStudent: async (userId, status) => {
    try {
      const response = await API.put(`/auth/verify/${userId}`, { status });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update student
  updateStudent: async (studentId, studentData) => {
    try {
      const response = await API.put(
        `/admin/students/${studentId}`,
        studentData,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete student
  deleteStudent: async (studentId) => {
    try {
      const response = await API.delete(`/admin/students/${studentId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get students by course
  getStudentsByCourse: async (courseId) => {
    try {
      const response = await API.get(`/admin/students/by-course/${courseId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get student's enrolled courses with payment info
  getStudentCourses: async (studentId) => {
    try {
      const response = await API.get(`/student/${studentId}/courses`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get student's transaction history
  getStudentTransactions: async (studentId) => {
    try {
      const response = await API.get(`/student/${studentId}/transactions`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get student's payment summary
  getStudentSummary: async (studentId) => {
    try {
      const response = await API.get(`/student/${studentId}/summary`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Make a payment (student pays)
  makePayment: async (studentId, paymentData) => {
    try {
      console.log("💳 Making payment for student:", studentId);
      console.log("📦 Payment data:", paymentData);

      const response = await API.post(
        `/students/${studentId}/payment`,
        paymentData,
      );

      console.log("✅ Payment response:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ Payment error:", error.response?.data || error.message);
      throw error;
    }
  },

  // Create student only (admin)
  createStudentOnly: async (studentData) => {
    try {
      const response = await API.post("/admin/students/create", studentData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  // Assign student to batch and auto-verify
  assignToBatch: async (studentId, batchId) => {
    try {
      const response = await API.post(
        `/admin/students/${studentId}/assign-batch`,
        {
          batch_id: batchId,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get student's assigned batches
  getStudentBatches: async (studentId) => {
    try {
      const response = await API.get(`/admin/students/${studentId}/batches`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  // Export students list
  exportStudents: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      params.append("type", "students");
      if (filters.search) params.append("search", filters.search);
      if (filters.status && filters.status !== "all")
        params.append("status", filters.status);
      if (filters.sort_by) params.append("sort_by", filters.sort_by);
      if (filters.sort_order) params.append("sort_order", filters.sort_order);

      const response = await API.get(`/export?${params.toString()}`, {
        responseType: "blob",
      });

      return ExportHelper.downloadFile(response, "students_export.csv");
    } catch (error) {
      throw error;
    }
  },

  // Export fee management list
  exportFees: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      params.append("type", "fees");
      if (filters.search) params.append("search", filters.search);
      if (filters.filter_due) params.append("filter_due", filters.filter_due);
      if (filters.sort_by) params.append("sort_by", filters.sort_by);

      const response = await API.get(`/export?${params.toString()}`, {
        responseType: "blob",
      });

      return ExportHelper.downloadFile(response, "fee_management.csv");
    } catch (error) {
      throw error;
    }
  },
  // Apply discount to student's course enrollment
  applyDiscount: async (studentId, discountData) => {
    try {
      const response = await API.post(
        `/admin/students/${studentId}/discount`,
        discountData,
      );
      return response.data;
    } catch (error) {
      console.error(
        "API Error - applyDiscount:",
        error.response?.data || error,
      );
      throw error;
    }
  },
};

export default StudentService;
